import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { Toaster } from 'react-hot-toast';

// Components
import Header from './components/Header';
import ChatInterface from './components/ChatInterface';
import Dashboard from './pages/Dashboard';
import NutritionLog from './pages/NutritionLog';
import WorkoutPlanner from './pages/WorkoutPlanner';
import Analytics from './pages/Analytics';
import Profile from './pages/Profile';

// Theme
const theme = {
  colors: {
    primary: '#1a1a1a',      // Dark primary (Ozymandias black)
    secondary: '#DAA520',     // Gold accent (Pyramid gold)
    background: '#f5f5f5',   // Light background
    surface: '#ffffff',      // Card backgrounds
    text: '#333333',         // Primary text
    textLight: '#666666',    // Secondary text
    success: '#4CAF50',      // Success green
    warning: '#FF9800',      // Warning orange
    error: '#f44336',        // Error red
    border: '#e0e0e0'        // Border color
  },
  fonts: {
    primary: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    secondary: '"Space Mono", monospace'
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px'
  },
  shadows: {
    light: '0 2px 4px rgba(0,0,0,0.1)',
    medium: '0 4px 8px rgba(0,0,0,0.15)',
    heavy: '0 8px 16px rgba(0,0,0,0.2)'
  }
};

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: ${props => props.theme.fonts.primary};
    color: ${props => props.theme.colors.text};
    background-color: ${props => props.theme.colors.background};
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1, h2, h3, h4, h5, h6 {
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
    line-height: 1.3;
  }

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: ${props => props.theme.colors.secondary};
    }
  }

  button {
    cursor: pointer;
    border: none;
    outline: none;
    font-family: inherit;
    transition: all 0.2s ease;
  }

  input, textarea, select {
    font-family: inherit;
    outline: none;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 14px;
    transition: border-color 0.2s ease;

    &:focus {
      border-color: ${props => props.theme.colors.primary};
    }
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 15px;
  }
`;

const ChatContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    bottom: 15px;
    right: 15px;
    left: 15px;
  }
`;

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Router>
        <AppContainer>
          <Header />
          
          <MainContent>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/nutrition" element={<NutritionLog />} />
              <Route path="/workout" element={<WorkoutPlanner />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/profile" element={<Profile />} />
            </Routes>
          </MainContent>

          <ChatContainer>
            <ChatInterface />
          </ChatContainer>

          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: theme.colors.surface,
                color: theme.colors.text,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '8px',
                boxShadow: theme.shadows.medium
              }
            }}
          />
        </AppContainer>
      </Router>
    </ThemeProvider>
  );
}

export default App;